import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ProviderCard } from "@/components/search/ProviderCard";
import { cityFromSlug, citySlug, insuranceFromSlug, specialtySlug } from "@/lib/seo-slugs";
import { searchProviders, type Provider, SPECIALTIES } from "@/data/heallexa";
import { upsertLandingPage } from "@/lib/seo-pages.functions";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/insurance/$slug/$city")({
 loader: async ({ params }) => {
 await setSeoPageCache();
 const insurance = insuranceFromSlug(params.slug);
 const city = cityFromSlug(params.city);
 if (!insurance || !city) throw notFound();

 let providers: Provider[] = [];
 try {
 providers = await searchProviders({
 insurance: insurance.short,
 location: city,
 limit: 25,
 });
 } catch {
 providers = [];
 }

 // Return a proper 404 for city+insurance combos with no providers.
 // A 200 with "we're still building" content is a soft 404 in Google's
 // eyes and wastes crawl budget. The URL stays in seo_pages for
 // future re-crawl once providers are added.
 if (providers.length === 0) throw notFound();

 const slug = `/insurance/${insurance.id}/${citySlug(city)}`;
 const title = `${providers.length}+ Doctors That Accept ${insurance.name} in ${city} | Heallexa`;
 const description = `Find in-network providers that accept ${insurance.name} in ${city}. Compare doctors, specialists, dentists, and therapists, read reviews, and book online \u2014 free, no signup, on Heallexa.`;

 try {
 await upsertLandingPage({
 data: {
 page_type: "insurance_city",
 slug,
 insurance_id: insurance.id,
 city,
 provider_count: providers.length,
 title,
 description,
 },
 });
 } catch {
 /* non-fatal */
 }

 return { insurance, city, providers, title, description, slug };
 },
 head: ({ loaderData }) => {
 const title = loaderData?.title ?? "Heallexa";
 const description = loaderData?.description ?? "";
 const url = `${SITE}${loaderData?.slug ?? ""}`;
 const providers = loaderData?.providers ?? [];
 const insurance = loaderData?.insurance;
 const city = loaderData?.city ?? "";
 const cityShort = city.split(",")[0];
 const stateShort = (city.split(",")[1] ?? "").trim();

 const meta: Array<Record<string, string>> = [
 { title },
 { name: "description", content: description },
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: url },
 { property: "og:type", content: "website" },
 ];

 const scripts = insurance
 ? [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "CollectionPage",
 name: title,
 description,
 url,
 numberOfItems: providers.length,
 about: { "@type": "HealthInsurancePlan", name: insurance.name },
 areaServed: {
 "@type": "City",
 name: cityShort,
 ...(stateShort
 ? { containedInPlace: { "@type": "State", name: stateShort } }
 : {}),
 },
 isPartOf: { "@id": `${SITE}/#website` },
 }),
 },
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: SITE },
 { "@type": "ListItem", position: 2, name: "Insurance", item: `${SITE}/insurance` },
 {
 "@type": "ListItem",
 position: 3,
 name: insurance.short,
 item: `${SITE}/insurance/${insurance.id}`,
 },
 { "@type": "ListItem", position: 4, name: cityShort, item: url },
 ],
 }),
 },
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "FAQPage",
 mainEntity: [
 {
 "@type": "Question",
 name: `How do I find a doctor that accepts ${insurance.name} in ${cityShort}?`,
 acceptedAnswer: {
 "@type": "Answer",
 text: `Use Heallexa to filter providers in ${cityShort} by ${insurance.name} acceptance. Always confirm your in-network status directly with the provider before booking.`,
 },
 },
 {
 "@type": "Question",
 name: `Does ${insurance.name} cover specialist visits in ${cityShort}?`,
 acceptedAnswer: {
 "@type": "Answer",
 text: `Most ${insurance.name} plans cover specialist visits, though copays and referral requirements vary. Contact your plan or the provider's billing office to confirm coverage.`,
 },
 },
 {
 "@type": "Question",
 name: `Is it free to search ${insurance.name} providers on Heallexa?`,
 acceptedAnswer: {
 "@type": "Answer",
 text: `Yes. Searching, comparing, and contacting providers on Heallexa is completely free for patients \u2014 no account required.`,
 },
 },
 ],
 }),
 },
 ]
 : [];

 return {
 meta,
 links: [{ rel: "canonical", href: url }],
 scripts,
 };
 },
 component: InsuranceCityPage,
});

function InsuranceCityPage() {
 const { insurance, city, providers } = Route.useLoaderData();
 const cityShort = city.split(",")[0];
 const stateShort = (city.split(",")[1] ?? "").trim();

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
 <nav className="text-sm text-muted-foreground mb-6">
 <a href="/sitemap" className="hover:text-primary">Sitemap</a>
 <span className="mx-2">/</span>
 <a href="/insurance" className="hover:text-primary">Insurance</a>
 <span className="mx-2">/</span>
 <Link to="/insurance/$slug" params={{ slug: insurance.id }} className="hover:text-primary">
 {insurance.short}
 </Link>
 <span className="mx-2">/</span>
 <span className="text-foreground">{cityShort}</span>
 </nav>

 <Reveal from="up">
 <header className="mb-12">
 <div className="inline-flex items-center gap-2 mb-4">
 <span
 className="h-8 w-8 rounded-md flex items-center justify-center text-xs font-semibold text-white"
 style={{ backgroundColor: insurance.color }}
 aria-hidden
 >
 {insurance.short.slice(0, 2).toUpperCase()}
 </span>
 <span className="font-display text-[13px] uppercase tracking-[0.15em] text-muted-foreground">{insurance.name}</span>
 </div>
 <h1 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground">
 Doctors that accept {insurance.name} in {cityShort}
 {stateShort ? `, ${stateShort}` : ""}
 </h1>
 <p className="mt-3 text-[14px] text-muted-foreground max-w-[560px]">
 {providers.length}+ in-network providers in {city}. Compare, check ratings, and book, free on Heallexa.
 </p>
 <div className="mt-6 flex flex-wrap gap-3">
 <Link
 to="/insurance/$slug"
 params={{ slug: insurance.id }}
 className="inline-flex items-center justify-center gap-2 h-11 px-5 text-[14px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-md"
 >
 <ShieldCheck className="h-4 w-4" />
 All {insurance.short} providers
 </Link>
 </div>
 </header>
 </Reveal>

 <section className="mb-16">
 <div className="flex items-end justify-between gap-6 mb-8">
 <div>
 <p className="font-display text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-3">
 In-network near you
 </p>
 <h2 className="font-display text-[clamp(1.4rem,2.4vw,1.8rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground">
 In-network providers near {cityShort}
 </h2>
 </div>
 </div>
 <ul className="grid gap-5">
 {providers.slice(0, 24).map((p: any, idx: number) => (
 <Reveal key={p.id} as="li" from="up" delay={Math.min(idx, 8) * 70}>
 <ProviderCard p={p} />
 </Reveal>
 ))}
 </ul>
 </section>

 <Reveal as="section" from="up" delay={60}>
 <h2 className="font-display text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
 {insurance.short} by specialty in {cityShort}
 </h2>
 <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6">
 {SPECIALTIES.slice(0, 24).map((s) => (
 <li key={s}>
 <a
 href={`/specialty/${specialtySlug(s)}/${citySlug(city)}`}
 className="inline-flex items-center gap-2 text-[14px] text-muted-foreground hover:text-primary transition-colors"
 >
 {s} in {city.split(",")[0]}
 </a>
 </li>
 ))}
 </ul>
 </Reveal>
 </main>
 <FooterSection />
 </div>
 );
}
